'use client';
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { UploadProps } from './data';

import React from 'react';
import { X } from 'react-feather';
import { useController } from 'react-hook-form';
import { useDisclosure } from '@chakra-ui/react';

import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  Tooltip,
} from '@/components/core';
import { useTranslation } from '@/hooks';
import AttachIcon from '@/svgs/Attachment.svg';
import Hint from '@/svgs/hint.svg';
import { standaloneToast, toBase64 } from '@/utils';

const Uploader = React.forwardRef<HTMLInputElement, UploadProps>(
  (
    {
      multiple = false,
      limit = 1,
      accept,
      variant,
      id,
      label,
      imageOnly,
      error,
      ...rest
    },
    forwardedRef
  ) => {
    const defaultAccept = imageOnly
      ? 'image/jpeg, image/png, image/png, image/svg, image/jpg, image/jpeg, image/gif, image/webp'
      : 'application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/vnd.ms-powerpoint,vnd.openxmlformats-officedocument.presentationml.presentation, data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, video/mp4, text/csv, text/doc, text/docx, text/xlsx, text/xls, text/pptx, text/ppt, text/pdf, image/png, image/svg+xml, image/jpg, image/jpeg, image/gif, image/webp';

    const { t } = useTranslation('components.uploader');
    const ref = React.useRef<HTMLInputElement>(null);
    const [files, setFiles] = React.useState<File[]>([]);

    const { isOpen, onOpen, onToggle, onClose } = useDisclosure();

    const control = useController({
      control: rest.control,
      name: rest.name,
    });

    React.useEffect(() => {
      if (
        !control.field?.value ||
        (control.field?.value as string)?.length < 1
      ) {
        setFiles([]);
      }
    }, [control.field.value]);

    const updateFormHandler = async ({ list }: { list: File[] }) => {
      await Promise.all(
        Object.values(list).map(async (item) => {
          const base64 = await toBase64(item);
          return base64 as string;
        })
      ).then((result) => {
        if (multiple) {
          control.field.onChange(result);
        } else {
          control.field.onChange(result[0]);
        }
      });
    };

    const removeFileHandler = ({ file }: { file: File }) => {
      const filteredValue = files.filter((f) => f?.name !== file.name);
      setFiles(filteredValue);
      void updateFormHandler({ list: filteredValue });
    };

    const addFileHandler = ({ file }: { file: File | undefined }) => {
      if (!file) {
        return;
      }
      const duplicated = files.find((item) => item.name === file?.name);
      if (file && file?.size > 4000000) {
        standaloneToast({
          status: 'warning',
          title: t('fileSizeDescription'),
        });
      } else if (!duplicated) {
        setFiles((prev) => {
          return [...prev, file];
        });
        void updateFormHandler({
          list: [...files, file],
        });
      }
    };

    return (
      <>
        <FormControl isInvalid={Boolean(error)}>
          {label && <FormLabel>{label}</FormLabel>}
          <Flex>
            <Box
              flex={1}
              minH="44px"
              my="5px"
              p="1"
              cursor="pointer"
              border={variant === 'filled' ? '1.2px solid' : '1px solid'}
              borderColor={
                // eslint-disable-next-line no-nested-ternary
                error
                  ? 'error.500'
                  : variant === 'filled'
                    ? 'gray.200'
                    : 'gray.600'
              }
              bgColor={variant === 'filled' ? 'gray.200' : 'transparent'}
              borderRadius="md"
              onClick={(): void => {
                if (
                  (!multiple && files.length === 1) ||
                  (multiple && files.length === limit)
                ) {
                  return;
                }
                document.getElementById(id)?.click();
              }}
            >
              <InputGroup>
                <Box
                  display="flex"
                  padding="5px 10px"
                  mr={2}
                  ml="10"
                  color="gray.800"
                  borderRadius="curved"
                  alignItems="center"
                  gap={3}
                  flexWrap="wrap"
                >
                  {files &&
                    Object.entries(files).filter(([key, _]) => key !== 'length')
                      .length > 0 &&
                    Object.values(files).map((item) => (
                      <Tooltip label={item?.name} key={item.lastModified}>
                        <Stack
                          flexDir="row"
                          align="center"
                          px={2}
                          bgColor={variant === 'filled' ? 'white' : 'gray.200'}
                          borderRadius="md"
                          minH={6}
                        >
                          <Text
                            maxW="120px"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            fontSize="small"
                          >
                            {item?.name}
                          </Text>
                          <Icon
                            color="error.500"
                            as={X}
                            width={4}
                            height={4}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFileHandler({ file: item });
                            }}
                          />
                        </Stack>
                      </Tooltip>
                    ))}
                </Box>
                <InputRightElement h="24px" top="1">
                  <Icon
                    w={{ base: '17px', md: '20px' }}
                    h={{ base: '17px', md: '20px' }}
                    as={AttachIcon}
                  />
                </InputRightElement>
              </InputGroup>
              <InputGroup ref={ref} position="relative">
                <Input
                  ref={forwardedRef}
                  type="file"
                  id={id}
                  display="none"
                  h={{ base: '38px', md: '48px' }}
                  {...rest}
                  accept={accept || defaultAccept}
                  onChange={({ target: { files: file } }) => {
                    addFileHandler({ file: file?.[0] });
                  }}
                />
              </InputGroup>
            </Box>

            <Flex alignItems="center" flexDir="column" justifyContent="center">
              <Tooltip
                isOpen={isOpen}
                label={
                  multiple
                    ? `${t('maximum4files')} ${
                        imageOnly
                          ? `${t('imageFormat')} ${t('maximumFileSize')} ${t('send')}`
                          : `${t('allTypeFormat')} ${t('maximumFileSize')} ${t('send')}`
                      }`
                    : `${
                        imageOnly
                          ? `${t('imageFormat')} ${t('maximumFileSize')} ${t('send')}`
                          : `${t('allTypeFormat')} ${t('maximumFileSize')} ${t('send')}`
                      }`
                }
                placement="top"
                hasArrow
                p="11px 14px 11px 14px"
                borderRadius="6px"
                bgColor="yellow.500"
              >
                <Stack
                  mr="14px"
                  onMouseEnter={onOpen}
                  onMouseLeave={onClose}
                  onClick={onToggle}
                >
                  <Icon as={Hint} width={6} height={6} />
                </Stack>
              </Tooltip>
            </Flex>
          </Flex>

          <FormErrorMessage message={error || ''} />
        </FormControl>
      </>
    );
  }
);

Uploader.displayName = 'Uploader';
export default Uploader;
